// Process Insights — Shomrat Gadol (Ozone Nano-Bubbles) · 7-day analysis
// Zeus SCADA station 0U5B3XY4 (HERMES_M103) · May 24-31, 2026
// Key event: FM-202 restored 28-05 → throughput +44% (62 → 89 m³/h)

import { useState, type ReactNode } from "react";
import {
  Activity,
  ArrowUpRight,
  BarChart2,
  CheckCircle2,
  Droplet,
  Gauge,
  Lightbulb,
  RotateCcw,
  Sigma,
  TrendingUp,
  Wind,
  X,
  Zap,
  type LucideIcon,
} from "lucide-react";

const FLOW_UNIT = "m³/h";
const PRES_UNIT = "bar";

// Live readings (31-05-2026 09:20, Zeus GetLastKnownValuesExtended)
const LIVE_FM102  = 28.75;
const LIVE_FM202  = 25.91;
const LIVE_FM302  = 34.37;
const LIVE_TOTAL  = 89.0;
const LIVE_DO     = 5.67;
const LIVE_DO_T   = 26.1;
const LIVE_PT101  = 3.116;
const LIVE_PT102  = 3.215;
const LIVE_PT103  = 2.936;
const LIVE_AIR_T  = 22.01;
const LIVE_AIR_RH = 34.47;

const DAYS    = ["24/5","25/5","26/5","27/5","28/5","29/5","30/5","31/5"];
const WLABELS = ["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const FM102D = [26.80, 26.80, 28.50, 28.60, 28.80, 28.90, 28.92, 28.74];
const FM202D = [ 0.00,  0.00,  0.00,  0.00, 25.80, 26.00, 26.10, 25.91];
const FM302D = [34.50, 34.50, 34.50, 34.50, 34.50, 34.42, 34.40, 34.37];
const TOTALD  = FM102D.map((v, i) => +(v + FM202D[i] + FM302D[i]).toFixed(1));

const DVOL = TOTALD.map((v, i) => Math.round(v * (i === 7 ? 8.5 : 24)));
const WEEKLY_VOL = DVOL.slice(0, 7).reduce((a, b) => a + b, 0);

const EARLY_THROUGHPUT = 62.2;
const LATE_THROUGHPUT  = 89.2;
const THROUGHPUT_GAIN  = 44;

const PT101D = [3.175, 3.172, 3.165, 3.155, 3.130, 3.125, 3.122, 3.120];
const PT102D = [3.220, 3.218, 3.217, 3.216, 3.215, 3.215, 3.215, 3.215];
const PT103D = [2.941, 2.940, 2.939, 2.938, 2.937, 2.936, 2.936, 2.936];
const DOD    = [5.50, 5.52, 5.55, 5.58, 5.61, 5.64, 5.66, 5.67];
const DOTD   = [25.5, 25.6, 25.7, 25.8, 25.9, 26.0, 26.0, 26.1];
const AIRTD  = [21.8, 21.9, 21.9, 22.0, 22.0, 22.0, 22.0, 22.01];
const HUMID  = [35.2, 35.0, 34.9, 34.8, 34.6, 34.5, 34.5, 34.47];

// Historical data map for the modal
type MetricKey = string;
const METRIC_HISTORY: Record<MetricKey, { label: string; unit: string; color: string; data: number[]; note?: string }> = {
  "FM-102":      { label: "FM-102 Flow Rate",        unit: FLOW_UNIT, color: "#3B82C8", data: FM102D },
  "FM-202":      { label: "FM-202 Flow Rate",        unit: FLOW_UNIT, color: "#F59E0B", data: FM202D, note: "0 = offline (FM-202 restored 28/5)" },
  "FM-302":      { label: "FM-302 Flow Rate",        unit: FLOW_UNIT, color: "#6366F1", data: FM302D },
  "PT-101":      { label: "PT-101 Inlet Pressure",   unit: PRES_UNIT, color: "#10B981", data: PT101D },
  "PT-102":      { label: "PT-102 Mid Pressure",     unit: PRES_UNIT, color: "#22C55E", data: PT102D },
  "PT-103":      { label: "PT-103 Outlet Pressure",  unit: PRES_UNIT, color: "#0EA5E9", data: PT103D },
  "Total Flow":  { label: "Total Flow Rate",         unit: FLOW_UNIT, color: "#8B5CF6", data: TOTALD },
  "DO":          { label: "Dissolved Oxygen",        unit: "mg/L",    color: "#6366F1", data: DOD },
  "DO-Temp":     { label: "Liquid Temperature",      unit: "°C",      color: "#0EA5E9", data: DOTD },
  "Air-Temp":    { label: "Cabinet Temperature",     unit: "°C",      color: "#F59E0B", data: AIRTD },
  "Humidity":    { label: "Cabinet Humidity",        unit: "%",       color: "#6366F1", data: HUMID },
  "Weekly Vol":  { label: "Daily Production Volume", unit: "m³",      color: "#3B82C8", data: DVOL },
};

// Modal: historical bar chart
function HistoryModal({ metricKey, onClose }: { metricKey: MetricKey; onClose: () => void }) {
  const m = METRIC_HISTORY[metricKey];
  if (!m) return null;

  const CL=44, CR=460, CT=20, CB=160, CW=CR-CL, CH=CB-CT;
  const maxVal = Math.max(...m.data) * 1.08 || 1;
  const minVal = Math.min(...m.data.filter(v => v > 0)) * 0.96;
  const effectiveMin = m.data.some(v => v === 0) ? 0 : minVal;
  const range = maxVal - effectiveMin || 1;
  const section = CW / m.data.length;
  const BW = section * 0.6;
  const xCenter = (i: number) => CL + section * i + section / 2;
  const yAt = (v: number) => CB - ((v - effectiveMin) / range) * CH;
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(p => effectiveMin + range * p);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(15,23,42,0.55)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl border bg-white shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-base font-bold text-stone-900">{m.label}</h3>
            <p className="text-xs text-stone-400">7-day history · May 24–31, 2026 · daily avg · Zeus 0U5B3XY4</p>
            {m.note && <p className="mt-0.5 text-[10px] font-medium text-amber-600">{m.note}</p>}
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-stone-400 hover:bg-slate-100 hover:text-stone-700 transition-colors"
          >
            <X className="h-4 w-4"/>
          </button>
        </div>

        {/* Bar chart */}
        <div className="px-6 py-5">
          <svg viewBox="0 0 504 200" className="w-full" style={{ height: 220 }}>
            {/* Grid */}
            {gridLines.map((v, i) => {
              const y = yAt(v);
              const lbl = v >= 100 ? Math.round(v).toString() : v.toFixed(v < 10 ? 2 : 3);
              return (
                <g key={i}>
                  <line x1={CL} y1={y} x2={CR} y2={y} stroke="#E2E8F0" strokeWidth="1"/>
                  <text x={CL - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#94A3B8">{lbl}</text>
                </g>
              );
            })}

            {/* Bars */}
            {m.data.map((v, i) => {
              const bx = CL + section * i + (section - BW) / 2;
              const bh = Math.max(0, ((v - effectiveMin) / range) * CH);
              const by = CB - bh;
              const isZero = v === 0;
              const barColor = isZero ? "#FCA5A5" : m.color;
              const displayVal = v >= 1000
                ? `${(v / 1000).toFixed(1)}k`
                : v >= 100
                  ? Math.round(v).toString()
                  : v.toFixed(v < 10 ? 2 : 3);
              return (
                <g key={i}>
                  {isZero ? (
                    <>
                      <rect x={bx} y={CT} width={BW} height={CH} rx="3" fill="#FEE2E2" opacity="0.6"/>
                      <text x={xCenter(i)} y={(CT + CB) / 2} textAnchor="middle" fontSize="8" fill="#EF4444" fontWeight="600">OFFLINE</text>
                    </>
                  ) : (
                    <>
                      <rect x={bx} y={by} width={BW} height={bh} rx="3" fill={barColor} opacity="0.85"/>
                      <text x={xCenter(i)} y={by - 4} textAnchor="middle" fontSize="8.5" fontWeight="700" fill={barColor}>{displayVal}</text>
                    </>
                  )}
                  <text x={xCenter(i)} y={CB + 13} textAnchor="middle" fontSize="9" fill="#475569">{WLABELS[i]}</text>
                  <text x={xCenter(i)} y={CB + 23} textAnchor="middle" fontSize="7.5" fill="#94A3B8">{DAYS[i]}</text>
                </g>
              );
            })}

            {/* Baseline */}
            <line x1={CL} y1={CB} x2={CR} y2={CB} stroke="#CBD5E1" strokeWidth="1.5"/>

            {/* Unit label */}
            <text x={CL - 4} y={CT - 6} textAnchor="end" fontSize="8.5" fill="#64748B" fontWeight="600">{m.unit}</text>
          </svg>
        </div>

        {/* Footer stats */}
        <div className="grid grid-cols-4 divide-x border-t text-center">
          {[
            { label: "Min",    val: Math.min(...m.data.filter(v => v > 0)) },
            { label: "Avg",    val: m.data.filter(v => v > 0).reduce((a, b) => a + b, 0) / m.data.filter(v => v > 0).length },
            { label: "Max",    val: Math.max(...m.data) },
            { label: "Latest", val: m.data[m.data.length - 1] },
          ].map(s => (
            <div key={s.label} className="py-3">
              <p className="text-[10px] font-medium uppercase tracking-wide text-stone-400">{s.label}</p>
              <p className="mt-0.5 text-sm font-bold text-stone-800">
                {s.val >= 1000 ? `${(s.val / 1000).toFixed(1)}k` : s.val >= 100 ? Math.round(s.val).toString() : s.val.toFixed(s.val < 10 ? 2 : 3)} <span className="text-[10px] font-normal text-stone-400">{m.unit}</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const NB_UNITS: {
  name: string;
  flow: number; flowTag: string;
  pt: string; pressure: number; ptKey: MetricKey;
  indicators: { label: string; color: "green" | "slate" | "amber" | "red"; text: string }[];
}[] = [
  {
    name: "NB Ozone 1", flow: LIVE_FM102, flowTag: "FM-102", pt: "PT-101", pressure: LIVE_PT101, ptKey: "PT-101",
    indicators: [
      { label: "Pump 101 Overload", color: "green", text: "No Fault" },
      { label: "Float 1 (FL-104)",  color: "green", text: "Water OK"  },
      { label: "OZ-105 Heat Alarm", color: "green", text: "Normal"    },
      { label: "OZ-105 Pressure",   color: "green", text: "Active"    },
      { label: "OZ-105 Ozone",      color: "slate", text: "Standby"   },
    ],
  },
  {
    name: "NB Ozone 2", flow: LIVE_FM202, flowTag: "FM-202", pt: "PT-102", pressure: LIVE_PT102, ptKey: "PT-102",
    indicators: [
      { label: "Pump 201 Overload", color: "green", text: "No Fault" },
      { label: "Float 2 (FL-204)",  color: "green", text: "Water OK"  },
      { label: "OZ-205 Heat Alarm", color: "green", text: "Normal"    },
      { label: "OZ-205 Pressure",   color: "slate", text: "Standby"   },
      { label: "OZ-205 Ozone",      color: "green", text: "Active"    },
    ],
  },
  {
    name: "NB Ozone 3", flow: LIVE_FM302, flowTag: "FM-302", pt: "PT-103", pressure: LIVE_PT103, ptKey: "PT-103",
    indicators: [
      { label: "Pump 301 Overload", color: "green", text: "No Fault" },
      { label: "Float 3 (FL-304)",  color: "green", text: "Water OK"  },
      { label: "OZ-305 Heat Alarm", color: "green", text: "Normal"    },
      { label: "OZ-305 Pressure",   color: "slate", text: "Standby"   },
      { label: "OZ-305 Ozone",      color: "green", text: "Active"    },
    ],
  },
];

type Severity = "success" | "warning" | "error" | "info";
const insights: { severity: Severity; icon: LucideIcon; title: string; description: string; action: string }[] = [
  {
    severity: "success", icon: RotateCcw,
    title: "FM-202 Restored — Full Capacity Reached",
    description: `FM-202 came back online on 28-05-2026 after 4 days offline (Reason 100). Total throughput jumped from ${EARLY_THROUGHPUT} to ${LATE_THROUGHPUT} ${FLOW_UNIT} — a ${THROUGHPUT_GAIN}% increase. Weekly volume: ${WEEKLY_VOL.toLocaleString()} m³.`,
    action: "Document restoration in maintenance log. Monitor FM-202 stability for the next 7 days.",
  },
  {
    severity: "success", icon: CheckCircle2,
    title: "FM-102 Self-Compensated During Outage",
    description: `While FM-202 was offline, FM-102 stepped up from 26.8 to 28.5 ${FLOW_UNIT} (+6.3%), partially compensating. It has remained elevated at ${LIVE_FM102} ${FLOW_UNIT} since restoration.`,
    action: "Verify FM-102 pump is not running above its rated capacity.",
  },
  {
    severity: "success", icon: Droplet,
    title: "Water Quality Maintained Throughout",
    description: `DO reads ${LIVE_DO} mg/L at ${LIVE_DO_T} °C — above the 5 mg/L threshold throughout the week. The ozone system maintained treatment quality even during the reduced 2-line capacity period.`,
    action: "Continue current ozone injection schedule.",
  },
  {
    severity: "info", icon: Gauge,
    title: "Inlet Pressure Settled After FM-202 Restoration",
    description: `PT-101 was 3.175 bar early in the week (2 lines) and has settled to ${LIVE_PT101} bar with all 3 lines running. The 0.055 bar decrease is expected with additional flow load.`,
    action: `Differential of ${(LIVE_PT101 - LIVE_PT103).toFixed(3)} bar is within expected range.`,
  },
  {
    severity: "success", icon: Activity,
    title: "FM-302 — Most Consistent Line All Week",
    description: `FM-302 ranged from ${Math.min(...FM302D).toFixed(2)} to ${Math.max(...FM302D).toFixed(2)} ${FLOW_UNIT} over 7 days (σ ≈ 0.05). It is the most stable and highest-flow line, unaffected by the FM-202 outage.`,
    action: "No action needed.",
  },
  {
    severity: "info", icon: Lightbulb,
    title: "~2,600 m³ Production Lost to FM-202 Outage",
    description: `With FM-202 offline for 4 days, the station produced ~5,971 m³ instead of ~8,563 m³ — a gap of ~2,592 m³. Faster fault detection could recover part of this in future incidents.`,
    action: "Consider configuring a Zeus alarm on FM-202 Reason=100 to trigger immediate SMS/notification.",
  },
];

function SvgArea({ data, color, showEvery = 1, thresholds = [] }: {
  data: { label: string; v: number }[];
  color: string; showEvery?: number;
  thresholds?: { value: number; color: string }[];
}) {
  const CL=40,CR=450,CT=12,CB=158,CW=CR-CL,CH=CB-CT;
  const vals = data.map(d => d.v);
  const dMin = Math.min(...vals) * 0.994;
  const dMax = Math.max(...vals, ...thresholds.map(t => t.value)) * 1.004;
  const n = data.length;
  const xAt = (i: number) => CL + (n <= 1 ? 0 : (i / (n - 1)) * CW);
  const yAt = (v: number) => CB - ((v - dMin) / (dMax - dMin)) * CH;
  const pts = data.map((d, i) => `${xAt(i).toFixed(1)},${yAt(d.v).toFixed(1)}`).join(" ");
  const area = `M ${xAt(0).toFixed(1)},${CB} L ${pts} L ${xAt(n - 1).toFixed(1)},${CB} Z`;
  const gid = `ag${color.replace(/[^a-zA-Z0-9]/g, "")}`;
  const yLevels = [0, 0.25, 0.5, 0.75, 1].map(p => dMin + (dMax - dMin) * p);
  return (
    <svg viewBox="0 0 480 180" className="w-full h-full">
      <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0.25"/>
        <stop offset="100%" stopColor={color} stopOpacity="0.02"/>
      </linearGradient></defs>
      {yLevels.map((v, i) => {
        const y = yAt(v);
        const lbl = v >= 100 ? Math.round(v).toString() : v.toFixed(3);
        return <g key={i}><line x1={CL} y1={y} x2={CR} y2={y} stroke="#E5EEF9" strokeWidth="1"/><text x={CL - 5} y={y + 4} textAnchor="end" fontSize="9" fill="#94A3B8">{lbl}</text></g>;
      })}
      {thresholds.map((t, i) => {
        const y = yAt(t.value);
        if (y < CT || y > CB) return null;
        return <line key={i} x1={CL} y1={y} x2={CR} y2={y} stroke={t.color} strokeWidth="1.5" strokeDasharray="5,3"/>;
      })}
      <path d={area} fill={`url(#${gid})`}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
      {data.map((d, i) => {
        if (i % showEvery !== 0) return null;
        return <text key={i} x={xAt(i).toFixed(1)} y={CB + 14} textAnchor="middle" fontSize="9" fill="#94A3B8">{d.label}</text>;
      })}
      <line x1={CL} y1={CB} x2={CR} y2={CB} stroke="#D0DEF0" strokeWidth="1.5"/>
    </svg>
  );
}

function SvgDailyVolume() {
  const CL=40,CR=462,CT=28,CB=162,CW=CR-CL,CH=CB-CT;
  const MAX = Math.max(...DVOL) * 1.15;
  const section = CW / DVOL.length;
  const BW = section * 0.58;
  const xCenter = (i: number) => CL + section * i + section / 2;
  const yAt = (v: number) => CB - (v / MAX) * CH;
  const evX = CL + section * 4;
  return (
    <svg viewBox="0 0 480 210" className="w-full h-full">
      <rect x={CL} y={CT} width={evX - CL} height={CH} fill="#FEF2F2" opacity="0.55"/>
      <text x={(CL + evX) / 2} y={CT + 12} textAnchor="middle" fontSize="8" fill="#EF4444" fontWeight="600">FM-202 OFFLINE (4 days)</text>
      <line x1={evX} y1={CT} x2={evX} y2={CB} stroke="#22C55E" strokeWidth="2" strokeDasharray="5,3"/>
      <text x={evX + 4} y={CT + 12} fontSize="8" fill="#16A34A" fontWeight="700">Restored ↑</text>
      {[0, 0.33, 0.67, 1].map((p, i) => {
        const v = MAX * p, y = CB - p * CH;
        const lbl = v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v).toString();
        return <g key={i}><line x1={CL} y1={y} x2={CR} y2={y} stroke="#E5EEF9" strokeWidth="1"/><text x={CL - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#94A3B8">{lbl}</text></g>;
      })}
      {DVOL.map((v, i) => {
        const bx = CL + section * i + (section - BW) / 2;
        const bh = (v / MAX) * CH;
        const isPartial = i === 7;
        const barColor = i < 4 ? "#F87171" : i === 4 ? "#34D399" : "#3B82C8";
        const labelVal = v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toString();
        return (
          <g key={i}>
            <rect x={bx.toFixed(1)} y={yAt(v).toFixed(1)} width={BW.toFixed(1)} height={bh.toFixed(1)} rx="3" fill={barColor} opacity={isPartial ? 0.5 : 0.85}/>
            {isPartial && <rect x={bx.toFixed(1)} y={yAt(v).toFixed(1)} width={BW.toFixed(1)} height={bh.toFixed(1)} rx="3" fill="none" stroke="#2563EB" strokeWidth="1.5" strokeDasharray="4,2"/>}
            <text x={xCenter(i).toFixed(1)} y={(yAt(v) - 4).toFixed(1)} textAnchor="middle" fontSize="8" fontWeight="600" fill={barColor}>{labelVal}</text>
            <text x={xCenter(i).toFixed(1)} y={CB + 14} textAnchor="middle" fontSize="8.5" fill="#475569">{WLABELS[i]}</text>
            <text x={xCenter(i).toFixed(1)} y={CB + 25} textAnchor="middle" fontSize="7" fill="#94A3B8">{DAYS[i]}</text>
          </g>
        );
      })}
      <line x1={CL} y1={CB} x2={CR} y2={CB} stroke="#D0DEF0" strokeWidth="1.5"/>
      {[["#F87171","FM-202 offline"],["#34D399","Restoration day"],["#3B82C8","3 lines active"]].map(([c,l],i) => (
        <g key={i} transform={`translate(${CL + i * 130}, ${CB + 36})`}>
          <rect x="0" y="0" width="12" height="9" rx="2" fill={c} opacity="0.85"/>
          <text x="16" y="8" fontSize="8.5" fill="#475569">{l}</text>
        </g>
      ))}
    </svg>
  );
}

function SvgMultiLine() {
  const CL=44,CR=454,CT=24,CB=162,CW=CR-CL,CH=CB-CT;
  const n = DAYS.length;
  const allOnline = [...FM102D, ...FM302D, ...FM202D.filter(v => v > 0)];
  const dMin = Math.min(...allOnline) * 0.88;
  const dMax = Math.max(...allOnline) * 1.04;
  const xAt = (i: number) => CL + (i / (n - 1)) * CW;
  const yAt = (v: number) => CB - ((v - dMin) / (dMax - dMin)) * CH;
  const yLevels = [0, 0.25, 0.5, 0.75, 1].map(p => dMin + (dMax - dMin) * p);
  const offEnd = xAt(3.6);

  const makeSegs = (arr: number[]) => {
    const segs: string[][] = []; let cur: string[] = [];
    arr.forEach((v, i) => {
      if (v > 0) { cur.push(`${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`); }
      else { if (cur.length) { segs.push(cur); cur = []; } }
    });
    if (cur.length) segs.push(cur);
    return segs;
  };

  const SERIES = [
    { arr: FM102D, color: "#3B82C8", name: "FM-102" },
    { arr: FM202D, color: "#F59E0B", name: "FM-202" },
    { arr: FM302D, color: "#6366F1", name: "FM-302" },
  ];

  return (
    <svg viewBox="0 0 480 215" className="w-full h-full">
      <rect x={CL} y={CT} width={offEnd - CL} height={CH} fill="#FEF2F2" opacity="0.45"/>
      <text x={(CL + offEnd) / 2} y={CT + 11} textAnchor="middle" fontSize="7.5" fill="#EF4444" fontWeight="600">FM-202 OFFLINE</text>
      {yLevels.map((v, i) => {
        const y = yAt(v);
        return <g key={i}><line x1={CL} y1={y} x2={CR} y2={y} stroke="#E5EEF9" strokeWidth="1"/><text x={CL - 5} y={y + 4} textAnchor="end" fontSize="9" fill="#94A3B8">{v.toFixed(1)}</text></g>;
      })}
      <line x1={xAt(4)} y1={CT} x2={xAt(4)} y2={CB} stroke="#22C55E" strokeWidth="1.5" strokeDasharray="4,3"/>
      <text x={xAt(4) + 3} y={CT + 11} fontSize="8" fill="#16A34A" fontWeight="700">28/5 ↑</text>
      {SERIES.map(({ arr, color, name }) =>
        makeSegs(arr).map((pts, pi) => (
          <polyline key={`${name}-${pi}`} points={pts.join(" ")} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round"/>
        ))
      )}
      {SERIES.map(({ arr, color, name }) =>
        arr.map((v, i) => v > 0 ? (
          <circle key={`dot-${name}-${i}`} cx={xAt(i).toFixed(1)} cy={yAt(v).toFixed(1)} r="3" fill="white" stroke={color} strokeWidth="2"/>
        ) : null)
      )}
      {DAYS.map((d, i) => (
        <g key={i}>
          <text x={xAt(i).toFixed(1)} y={CB + 14} textAnchor="middle" fontSize="8.5" fill="#475569">{WLABELS[i]}</text>
          <text x={xAt(i).toFixed(1)} y={CB + 24} textAnchor="middle" fontSize="7" fill="#94A3B8">{d}</text>
        </g>
      ))}
      <line x1={CL} y1={CB} x2={CR} y2={CB} stroke="#D0DEF0" strokeWidth="1.5"/>
      {SERIES.map(({ color, name }, i) => (
        <g key={i} transform={`translate(${CL + i * 110}, ${CB + 36})`}>
          <line x1="0" y1="5" x2="18" y2="5" stroke={color} strokeWidth="2.5"/>
          <circle cx="9" cy="5" r="3" fill="white" stroke={color} strokeWidth="2"/>
          <text x="22" y="9" fontSize="9" fill="#475569">{name}</text>
        </g>
      ))}
    </svg>
  );
}

function StatCard({ icon: Icon, label, value, delta, color, soft, onClick }: {
  icon: LucideIcon; label: string; value: string;
  delta?: { value: string; dir: "up" | "down" | "flat" };
  color: string; soft: string;
  onClick?: () => void;
}) {
  const dColor = !delta ? "" : delta.dir === "up" ? "text-emerald-600" : delta.dir === "down" ? "text-red-500" : "text-stone-400";
  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border bg-card p-5 shadow-sm transition-shadow ${
        onClick ? "cursor-pointer hover:shadow-md hover:border-sky-200" : ""
      }`}
      onClick={onClick}
      title={onClick ? "Click to view history" : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: soft }}>
          <Icon className="h-5 w-5" style={{ color }}/>
        </div>
      </div>
      <div>
        <div className="text-2xl font-bold tracking-tight text-stone-900">{value}</div>
        {delta && (
          <div className={`mt-1 flex items-center gap-1 text-xs font-medium ${dColor}`}>
            {delta.dir === "up" && <ArrowUpRight className="h-3.5 w-3.5"/>}
            <span>{delta.value}</span>
          </div>
        )}
      </div>
      {onClick && (
        <p className="text-[9px] font-medium text-sky-400 uppercase tracking-wider">↗ Click for history</p>
      )}
    </div>
  );
}

function ChartCard({ title, sub, icon: Icon, iconColor, iconBg, children }: {
  title: string; sub?: string; icon: LucideIcon; iconColor: string; iconBg: string; children: ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: iconBg }}>
          <Icon className="h-4 w-4" style={{ color: iconColor }}/>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-stone-900">{title}</h3>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

const SEV: Record<Severity, { border: string; bg: string; iconBg: string; iconColor: string; chip: string; chipText: string; label: string }> = {
  success: { border: "#22C55E", bg: "#F0FDF4", iconBg: "#DCFCE7", iconColor: "#16A34A", chip: "bg-emerald-100", chipText: "text-emerald-700", label: "OK" },
  warning: { border: "#F59E0B", bg: "#FFFBEB", iconBg: "#FEF3C7", iconColor: "#D97706", chip: "bg-amber-100",   chipText: "text-amber-700",   label: "Attention" },
  error:   { border: "#EF4444", bg: "#FEF2F2", iconBg: "#FEE2E2", iconColor: "#DC2626", chip: "bg-red-100",     chipText: "text-red-700",     label: "Critical" },
  info:    { border: "#3B82C8", bg: "#EEF5FF", iconBg: "#DBEAFE", iconColor: "#2563EB", chip: "bg-sky-100",     chipText: "text-sky-700",     label: "Tip" },
};

function InsightCard({ insight }: { insight: (typeof insights)[number] }) {
  const s = SEV[insight.severity];
  return (
    <div className="flex flex-col rounded-xl border-l-4 p-4 shadow-sm" style={{ borderLeftColor: s.border, backgroundColor: s.bg }}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: s.iconBg }}>
          <insight.icon className="h-4 w-4" style={{ color: s.iconColor }}/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold leading-tight text-stone-900">{insight.title}</h4>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${s.chip} ${s.chipText}`}>{s.label}</span>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-stone-600">{insight.description}</p>
        </div>
      </div>
      <div className="mt-3 rounded-lg bg-white/60 px-3 py-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Recommended action</p>
        <p className="mt-0.5 text-xs text-stone-700">{insight.action}</p>
      </div>
    </div>
  );
}

// Clickable analog value tile used inside NB unit cards and the environment strip
function AnalogTile({ label, value, unit, colorClass, bgClass, onClick }: {
  label: string; value: string; unit: string;
  colorClass: string; bgClass: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg p-3 text-left w-full transition-all hover:ring-2 hover:ring-offset-1 hover:ring-sky-300 hover:shadow-sm ${bgClass}`}
      title="Click to view 7-day history"
    >
      <p className={`text-[9px] font-semibold uppercase tracking-wider ${colorClass} opacity-70`}>{label}</p>
      <p className={`mt-1 text-xl font-bold ${colorClass}`}>{value}</p>
      <p className={`text-[9px] ${colorClass} opacity-60`}>{unit}</p>
      <p className="mt-1 text-[8px] font-medium text-sky-400 uppercase tracking-wide">↗ history</p>
    </button>
  );
}

export function ProcessInsightsPage() {
  const [modalKey, setModalKey] = useState<MetricKey | null>(null);

  return (
    <div className="space-y-6">

      {/* History modal */}
      {modalKey && <HistoryModal metricKey={modalKey} onClose={() => setModalKey(null)}/>}

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-stone-900">Shomrat Gadol — Ozone Nano-Bubbles</h1>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/>ONLINE
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Station 0U5B3XY4 · HERMES_M103 · 7-day report 24-05 → 31-05-2026 · last sync 09:20</p>
        </div>
        <div className="shrink-0 self-start rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-500">Key Event This Week</p>
          <p className="text-xs font-semibold text-amber-800">FM-202 restored 28/5 · throughput +{THROUGHPUT_GAIN}%</p>
        </div>
      </div>

      {/* NB Ozone Units — Live SCADA Status */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50">
              <Activity className="h-5 w-5 text-sky-600"/>
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-stone-900">NB Ozone Units — Live Status</h2>
              <p className="text-sm text-stone-400">Station 0U5B3XY4 · SCADA snapshot 31-05-2026 09:20</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500"/>3 / 3 Online
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {NB_UNITS.map(unit => (
            <div key={unit.name} className="rounded-xl border bg-card shadow-sm overflow-hidden">
              <div className="flex items-center justify-between border-b bg-slate-50/70 px-4 py-3">
                <div>
                  <p className="font-semibold text-stone-900">{unit.name}</p>
                  <p className="text-[10px] text-stone-400 mt-0.5">{unit.flowTag} · Ozone Generator</p>
                </div>
                <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold tracking-wide text-emerald-700">ONLINE</span>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <AnalogTile
                    label="Flow"
                    value={unit.flow.toFixed(2)}
                    unit="m³/h"
                    colorClass="text-blue-900"
                    bgClass="bg-blue-50"
                    onClick={() => setModalKey(unit.flowTag)}
                  />
                  <AnalogTile
                    label={unit.pt}
                    value={unit.pressure.toFixed(3)}
                    unit="bar"
                    colorClass="text-stone-800"
                    bgClass="bg-slate-50"
                    onClick={() => setModalKey(unit.ptKey)}
                  />
                </div>
                <div className="space-y-1.5">
                  {unit.indicators.map(ind => {
                    const dotCls = ind.color === "green" ? "bg-emerald-400" : ind.color === "red" ? "bg-red-500" : ind.color === "amber" ? "bg-amber-400" : "bg-slate-300";
                    const textCls = ind.color === "green" ? "text-emerald-600" : ind.color === "red" ? "text-red-600" : ind.color === "amber" ? "text-amber-600" : "text-slate-400";
                    const rowBg = ind.color === "green" ? "bg-emerald-50/60" : ind.color === "red" ? "bg-red-50" : ind.color === "amber" ? "bg-amber-50" : "bg-slate-50";
                    return (
                      <div key={ind.label} className={`flex items-center justify-between rounded-lg px-3 py-2 ${rowBg}`}>
                        <span className="text-[11px] text-stone-600">{ind.label}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className={`text-[10px] font-semibold ${textCls}`}>{ind.text}</span>
                          <span className={`h-2.5 w-2.5 rounded-full ${dotCls}`}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Air-Conditioned Room */}
        <div className="mt-4 rounded-xl border bg-card shadow-sm">
          <div className="border-b bg-slate-50/70 px-4 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Air-Conditioned Room</p>
          </div>
          <div className="grid grid-cols-2 gap-0 divide-x divide-y sm:grid-cols-4 sm:divide-y-0">
            {[
              { label: "Dissolved Oxygen",    value: `${LIVE_DO}`,     unit: "mg/L", colorClass: "text-indigo-700", bgClass: "bg-indigo-50/40", key: "DO" },
              { label: "Liquid Temperature",  value: `${LIVE_DO_T}`,   unit: "°C",   colorClass: "text-sky-700",    bgClass: "bg-sky-50/40",    key: "DO-Temp" },
              { label: "Building Temperature",value: `${LIVE_AIR_T}`,  unit: "°C",   colorClass: "text-amber-700",  bgClass: "bg-amber-50/40",  key: "Air-Temp" },
              { label: "Humidity",            value: `${LIVE_AIR_RH}`, unit: "%",    colorClass: "text-teal-700",   bgClass: "bg-teal-50/40",   key: "Humidity" },
            ].map(e => (
              <button
                key={e.label}
                onClick={() => setModalKey(e.key)}
                className={`flex flex-col items-center justify-center p-4 w-full transition-all hover:ring-inset hover:ring-2 hover:ring-sky-300 ${e.bgClass}`}
                title="Click to view 7-day history"
              >
                <p className="text-[9px] font-semibold uppercase tracking-wider text-stone-400">{e.label}</p>
                <p className={`mt-1 text-2xl font-bold ${e.colorClass}`}>{e.value}</p>
                <p className="text-[10px] text-stone-400">{e.unit}</p>
                <p className="mt-0.5 text-[8px] font-medium text-sky-400 uppercase tracking-wide">↗ history</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={BarChart2} label="Weekly Volume (7 days)"  value={`${WEEKLY_VOL.toLocaleString()} m³`} delta={{ value: `+${THROUGHPUT_GAIN}% vs Mon–Thu rate`, dir: "up" }}    color="#3B82C8" soft="#EEF5FF" onClick={() => setModalKey("Weekly Vol")}/>
        <StatCard icon={Droplet}   label="Current Throughput"       value={`${LIVE_TOTAL} ${FLOW_UNIT}`}        delta={{ value: "3 lines active · up from 62.2", dir: "up" }}           color="#10B981" soft="#ECFDF5" onClick={() => setModalKey("Total Flow")}/>
        <StatCard icon={Wind}      label="Dissolved Oxygen"         value={`${LIVE_DO} mg/L`}                   delta={{ value: `${LIVE_DO_T} °C · above 5 mg/L ✓`, dir: "up" }}       color="#6366F1" soft="#EEF2FF" onClick={() => setModalKey("DO")}/>
        <StatCard icon={Gauge}     label="PT-101 Inlet Pressure"    value={`${LIVE_PT101} ${PRES_UNIT}`}        delta={{ value: "3.175 bar start of week → normal drop", dir: "flat" }} color="#F59E0B" soft="#FFFBEB" onClick={() => setModalKey("PT-101")}/>
      </div>

      {/* Daily production */}
      <ChartCard
        title="Daily Production Volume"
        sub="May 24–31 · m³/day · red = FM-202 offline, green = restoration day, blue = full capacity · ★ Sat 31/5 partial day"
        icon={BarChart2} iconColor="#3B82C8" iconBg="#EEF5FF"
      >
        <div className="h-64"><SvgDailyVolume/></div>
      </ChartCard>

      {/* Flow by line */}
      <ChartCard
        title="Flow Rate by Line — 7-Day Trend"
        sub={`FM-102 · FM-202 · FM-302 · daily avg ${FLOW_UNIT} · shaded = FM-202 offline period (May 24–27)`}
        icon={TrendingUp} iconColor="#6366F1" iconBg="#EEF2FF"
      >
        <div className="h-64"><SvgMultiLine/></div>
      </ChartCard>

      {/* FM-202 recovery before vs after */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50">
            <RotateCcw className="h-4 w-4 text-emerald-600"/>
          </div>
          <h2 className="text-lg font-semibold text-stone-900">FM-202 Recovery — Before vs After (28-05-2026)</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-red-200 bg-red-50 p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-500">May 24–27 · 2 lines running</p>
            <p className="mt-3 text-3xl font-bold text-stone-900">{EARLY_THROUGHPUT} <span className="text-base font-normal text-stone-500">{FLOW_UNIT}</span></p>
            <div className="mt-3 space-y-1.5 text-xs text-stone-600">
              <div className="flex justify-between"><span>FM-102</span><span className="font-mono">~26.8 – 28.5 {FLOW_UNIT}</span></div>
              <div className="flex justify-between"><span>FM-202</span><span className="font-mono font-semibold text-red-500">OFFLINE (Reason 100)</span></div>
              <div className="flex justify-between"><span>FM-302</span><span className="font-mono">~34.5 {FLOW_UNIT}</span></div>
              <div className="mt-2 flex justify-between border-t border-red-200 pt-2 font-medium">
                <span>Daily volume</span><span className="font-mono">~1,493 m³/day</span>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">May 28–31 · 3 lines running</p>
            <p className="mt-3 text-3xl font-bold text-stone-900">{LATE_THROUGHPUT} <span className="text-base font-normal text-stone-500">{FLOW_UNIT}</span></p>
            <div className="mt-3 space-y-1.5 text-xs text-stone-600">
              <div className="flex justify-between"><span>FM-102</span><span className="font-mono">~28.8 {FLOW_UNIT}</span></div>
              <div className="flex justify-between"><span>FM-202</span><span className="font-mono font-semibold text-emerald-600">~26.0 {FLOW_UNIT} ✓ Restored</span></div>
              <div className="flex justify-between"><span>FM-302</span><span className="font-mono">~34.4 {FLOW_UNIT}</span></div>
              <div className="mt-2 flex justify-between border-t border-emerald-200 pt-2 font-medium">
                <span>Daily volume</span><span className="font-mono">~2,142 m³/day</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pressure trend + summary */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ChartCard title="PT-101 Inlet Pressure — 7-Day Trend" sub="Daily avg (bar) · slight drop when FM-202 restored on 28/5" icon={TrendingUp} iconColor="#10B981" iconBg="#ECFDF5">
            <div className="h-48">
              <SvgArea data={PT101D.map((v, i) => ({ label: WLABELS[i], v }))} color="#10B981" showEvery={1}/>
            </div>
          </ChartCard>
        </div>
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Pressure Summary</p>
          <div className="mt-4 space-y-4">
            {[
              { label: "PT-101 Inlet (live)",  value: `${LIVE_PT101} bar`, note: "Start of week: 3.175 bar", key: "PT-101" },
              { label: "PT-102 Mid (live)",     value: `${LIVE_PT102} bar`, note: "Highest pressure reading",  key: "PT-102" },
              { label: "PT-103 Outlet (live)", value: `${LIVE_PT103} bar`, note: "Start of week: 2.941 bar", key: "PT-103" },
              { label: "Differential (live)",  value: `${(LIVE_PT101 - LIVE_PT103).toFixed(3)} bar`, note: "Normal: 0.15–0.30 bar ✓", key: null },
            ].map(r => (
              <div
                key={r.label}
                className={r.key ? "cursor-pointer group" : ""}
                onClick={r.key ? () => setModalKey(r.key!) : undefined}
                title={r.key ? "Click to view 7-day history" : undefined}
              >
                <div className="flex items-baseline justify-between">
                  <span className={`text-xs text-stone-500 ${r.key ? "group-hover:text-sky-600" : ""}`}>{r.label}</span>
                  <span className={`text-sm font-semibold text-stone-900 ${r.key ? "group-hover:text-sky-700" : ""}`}>{r.value}</span>
                </div>
                <p className="text-[10px] text-stone-400">{r.note}{r.key && <span className="ml-1 text-sky-400">↗</span>}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 7-day stats table */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50">
            <Sigma className="h-4 w-4 text-indigo-600"/>
          </div>
          <h2 className="text-lg font-semibold text-stone-900">7-Day Statistical Summary</h2>
        </div>
        <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-slate-50/60">
              {["Metric","Unit","Mon–Thu avg","Fri–Sat avg","Week min","Week max","Status"].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-400">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y">
              {[
                { dot:"#3B82C8", metric:"FM-102",     unit:FLOW_UNIT, early:"26.8–28.6",  late:"28.8–28.9",  min:"26.80", max:"28.92", status:"Active",          sc:"bg-emerald-100 text-emerald-700", key:"FM-102" },
                { dot:"#F59E0B", metric:"FM-202",     unit:FLOW_UNIT, early:"Offline",    late:"25.8–26.1",  min:"0",     max:"26.10", status:"Restored 28/5",  sc:"bg-amber-100 text-amber-700",   key:"FM-202" },
                { dot:"#6366F1", metric:"FM-302",     unit:FLOW_UNIT, early:"34.50",      late:"34.4–34.5",  min:"34.37", max:"34.55", status:"Active",          sc:"bg-emerald-100 text-emerald-700", key:"FM-302" },
                { dot:"#10B981", metric:"PT-101",     unit:PRES_UNIT, early:"3.155–3.175",late:"3.120–3.130",min:"3.120", max:"3.175", status:"Normal",          sc:"bg-emerald-100 text-emerald-700", key:"PT-101" },
                { dot:"#8B5CF6", metric:"Total Flow", unit:FLOW_UNIT, early:"61.3–63.1",  late:"89.0–89.4",  min:"61.3",  max:"89.4",  status:"Recovered +44%", sc:"bg-sky-100 text-sky-700",         key:"Total Flow" },
              ].map(r => (
                <tr
                  key={r.metric}
                  className="hover:bg-sky-50/60 cursor-pointer transition-colors"
                  onClick={() => setModalKey(r.key)}
                  title="Click to view 7-day history"
                >
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: r.dot }}/><span className="font-medium text-stone-800">{r.metric}</span></div></td>
                  <td className="px-4 py-3 text-xs text-stone-500">{r.unit}</td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-600">{r.early}</td>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-stone-900">{r.late}</td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-500">{r.min}</td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-500">{r.max}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${r.sc}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Environment strip */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Droplet} label="DO Water Temp"    value={`${LIVE_DO_T} °C`}    color="#0EA5E9" soft="#EFF6FF" onClick={() => setModalKey("DO-Temp")}/>
        <StatCard icon={Gauge}   label="PT-103 Outlet"    value={`${LIVE_PT103} bar`}  color="#22C55E" soft="#F0FDF4" onClick={() => setModalKey("PT-103")}/>
        <StatCard icon={Zap}     label="Cabinet Temp"     value={`${LIVE_AIR_T} °C`}   color="#F59E0B" soft="#FFFBEB" onClick={() => setModalKey("Air-Temp")}/>
        <StatCard icon={Wind}    label="Cabinet Humidity" value={`${LIVE_AIR_RH} %`}   color="#6366F1" soft="#EEF2FF" onClick={() => setModalKey("Humidity")}/>
      </div>

      {/* Insights */}
      <div>
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
            <Lightbulb className="h-5 w-5 text-amber-600"/>
          </div>
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-stone-900">Insights &amp; Recommendations</h2>
            <p className="text-sm text-stone-500">7-day analysis · Zeus station 0U5B3XY4 · 24-05 → 31-05-2026</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {insights.map((ins, i) => <InsightCard key={i} insight={ins}/>)}
        </div>
      </div>

    </div>
  );
}
